import { useQuery, NetworkStatus } from '@apollo/client'
import { ReactElement, useEffect } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'

import { Loader } from '@10xLabs/react-web/components/Loader'
import { Button } from 'components/v2/Button'
import EmptyState from 'components/v2/EmptyState'
import FormLayout from 'components/v2/FormLayout'
import { ForbiddenView } from 'features/App/components/ForbiddenView'
import { withLastUrl } from 'features/App/components/WithLastUrl'
import { NewVehiclesV2 } from 'features/Vehicles/components/NewVehiclesV2Form'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { VehiclesV2FormUtils } from 'features/Vehicles/utils'
import { useLogin } from 'features/Login/hooks'
import { canWrite } from 'features/Users/utils'
import { GET_VEHICLE_V2 } from 'features/Vehicles/queries-legacy'
import { useParams } from 'react-router-dom'
import { GTTVehicle } from 'gql/types.generated'
import Heading from 'components/v2/Heading'

const messages = defineMessages({
  helmet: {
    id: 'vehiclesV2.edit.helmet',
    defaultMessage: 'Edit Vehicle'
  }
})

const EditVehiclesV2View = (): ReactElement => {
  const intl = useIntl()
  const login = useLogin()
  const { vehicleId } = useParams()
  const canEdit = canWrite(FEATURES.VEHICLE, login.accessToken.policies)

  useEffect(() => {
    document.title = intl.formatMessage(messages.helmet)
  }, [])

  const {
    loading: queryLoading,
    error: queryError,
    data: queryData,
    refetch,
    networkStatus
  } = useQuery<{ tvehicle: GTTVehicle }>(GET_VEHICLE_V2, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    skip: !canEdit,
    variables: {
      input: {
        id: vehicleId
      }
    }
  })

  if (!canEdit) {
    return <ForbiddenView feature={FEATURES.VEHICLE} />
  }

  let content: ReactElement
  if (networkStatus === NetworkStatus.refetch || queryLoading) {
    content = (
      <EmptyState.Container>
        <Loader />
      </EmptyState.Container>
    )
  } else if (queryError) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="vehiclesV2.edit.error.description"
            defaultMessage="There was an error while loading your data"
          />
        </EmptyState.Description>
        <EmptyState.Action>
          <Button
            onClick={(): void => {
              refetch()
            }}
            kind="primary"
          >
            <FormattedMessage id="vehiclesV2.edit.error.reload" defaultMessage="Reload" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else {
    content = (
      <NewVehiclesV2 initialValues={VehiclesV2FormUtils.mapResponseToForm(queryData?.tvehicle as GTTVehicle)} isEdit />
    )
  }

  return (
    <FormLayout.Container>
      <FormLayout.Content>
        <Heading.H3 capitalize>
          <FormattedMessage id="vehiclesV2.edit.title" defaultMessage="Edit vehicle" />
        </Heading.H3>
        {content}
      </FormLayout.Content>
    </FormLayout.Container>
  )
}

export default withLastUrl(EditVehiclesV2View)
